import { AlertTriangle, CheckCircle2, DatabaseBackup, RefreshCw, Server } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { ApiRequestError, apiRequest } from "../api";

type HealthResponse = { status: string };

export function BackupStatusPage() {
  const [serverHealthy, setServerHealthy] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    apiRequest<HealthResponse>("/health")
      .then((response) => setServerHealthy(response.status === "ok"))
      .catch((requestError: unknown) => {
        setServerHealthy(false);
        setError(requestError instanceof ApiRequestError ? requestError.message : "RBIM Server health could not be checked.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="portal-page">
      <header className="page-heading">
        <div><span className="eyebrow">Municipality operations</span><h1>Backup Status</h1><p>Check the RBIM server before backup and recovery operations, and clearly identify which backup evidence is available.</p></div>
        <button type="button" className="secondary-button" onClick={load} disabled={loading}><RefreshCw />Refresh</button>
      </header>

      {error ? <div className="form-error" role="alert">{error}</div> : null}

      <section className="maintenance-grid">
        <article className="module-card">
          <span className="module-icon"><Server /></span>
          <div><span className="eyebrow">Server health</span><h2>{loading ? "Checking server…" : serverHealthy ? "RBIM Server reachable" : "RBIM Server unavailable"}</h2><p>{serverHealthy ? "The API health endpoint is responding. This confirms server availability only; it does not by itself prove that a database backup succeeded." : "Resolve server connectivity before relying on operational backup checks."}</p>{!loading ? serverHealthy ? <span className="status-pill"><CheckCircle2 />Healthy</span> : <span className="badge"><AlertTriangle />Attention</span> : null}</div>
        </article>

        <article className="module-card">
          <span className="module-icon"><DatabaseBackup /></span>
          <div><span className="eyebrow">Backup telemetry</span><h2>External backup evidence required</h2><p>The current RBIM API does not publish a verified last-backup timestamp, backup size, storage location, or restore-verification timestamp. The system intentionally reports these fields as unavailable instead of assuming that a backup succeeded.</p><span className="badge"><AlertTriangle />Not reported by API</span></div>
        </article>
      </section>

      <section className="table-card">
        <div className="table-title"><DatabaseBackup /><div><h2>Backup acceptance evidence</h2><p>These items must be supplied by the deployment backup process before release sign-off.</p></div></div>
        <div className="responsive-table">
          <table>
            <thead><tr><th>Check</th><th>Current status</th><th>Required evidence</th></tr></thead>
            <tbody>
              <tr><td>API/server availability</td><td>{loading ? "Checking…" : serverHealthy ? "Available" : "Unavailable"}</td><td>Successful `/api/health` response</td></tr>
              <tr><td>Last successful PostgreSQL backup</td><td>Not reported</td><td>Timestamp and successful backup job result</td></tr>
              <tr><td>Backup size and storage target</td><td>Not reported</td><td>Verified backup artifact metadata outside the production database volume</td></tr>
              <tr><td>Restore verification</td><td>Not reported</td><td>Successful restore into a separate recovery database plus RBIM validation</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <div className="notice-card"><strong><AlertTriangle />Release gate</strong><p>Do not mark backup/restore readiness complete until a real PostgreSQL backup has been restored and verified against the target Municipality/City deployment.</p></div>
    </section>
  );
}
